import React, { useEffect, useLayoutEffect, useCallback, useRef, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { useLifeSpaces, useWindowState, useTabManager, type Tab } from '../hooks/multiWindow'
import { useQuickAddPrewarm } from '../hooks/useQuickAddPrewarm'
import { useNavigationHistory, type PageVisit } from '../hooks/multiWindow/useNavigationHistory'
import { deserializeTab, serializeTab } from '../hooks/multiWindow/tabSerialization'
import { labelPageHistory } from './pageHistoryLabels'
import { showAllToastsForDev } from './devToastDemo'
import { useTestDataMenuBridge } from './useTestDataMenuBridge'
import { TabBar } from '../components/navigation/TabBar'
import { LifeSpacesSidebar } from '../components/navigation/LifeSpacesSidebar'
import { StandardSidebar } from '../components/navigation/StandardSidebar'
import { RightSidebar, isGoalsPath, isRightSidebarVisible } from '../components/navigation/RightSidebar'
import { useCanvasFocus, useCanvasMode, useDetailsListMinWidth, useDetailsPanelOpen, usePageListMinWidth } from '../contexts/CanvasFocusContext'
import { useTitlebarContext } from '../components/layout/Titlebar'
import { ContentUnionShape } from '../components/layout/ContentUnionShape'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronRight, CircleCheckBig, type LucideIcon } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbPage,
} from '../components/ui/breadcrumb'
import { growMotion } from '../lib/motion/growMotion'
import { whenCalm } from '../lib/motion/whenCalm'
import { useTranslation } from 'react-i18next'
import { usePlatform } from '../hooks/usePlatform'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'
import { rememberLastProject } from '../lib/lastProject'
import { rememberOpenProject } from '../lib/openProject'
import { openFeedbackForm } from '../lib/sentry'
import { onAppEvent } from '../lib/events'
import { AreaIcon } from '../components/icons/AreaIcon'
import { RecurringIcon } from '../components/icons/RecurringIcon'
import { DormantIcon, QuickNotesFilledIcon, UpcomingIcon } from '../components/icons'
import { GoalsIcon } from '../components/icons/GoalsIcon'
import { CommandPaletteDialog } from '../components/common/CommandPalette'
import { TestDataConfirmDialog } from '../components/common/TestData'
import { useCommandPaletteShortcut } from '../hooks/useCommandPaletteShortcut'
import { useQuickAddKey } from '../hooks/useQuickAddKey'
import { usePlayTask } from '../lib/focus'
import { recordRecent } from '../lib/recent/recentItems'
import { openSearchHit, type SearchHit, type SearchPage, type SearchTarget } from '../lib/taskJump/openSearchHit'
import type { LifeSpace } from '../lib/db/models'
import { DiscoverTip } from '../components/common/DiscoverTip'
import { useIntroNote } from '../lib/intro'
import { QuickAddDialog, requestRecurringCreate, useChildRecurrenceNotice, useIntroStart, useQuickAddCreatedToast, useQuickAddReveal, useReminderDelivery } from '../features/taskManagement'
import { useAutoScrollbar } from '../hooks/useAutoScrollbar'
import { db } from '../lib/db/indexeddb'
import { TabManagerProvider } from '../contexts/TabManagerContext'
import { BreadcrumbTitleProvider, useBreadcrumbTitle } from '../contexts/BreadcrumbTitleContext'
import { usePreferencesDialog } from '../contexts/PreferencesDialogContext'
import { useWindowMode } from '../contexts/WindowModeProvider'
import { useModeSwitchChoreography } from './useModeSwitchChoreography'
import { isNavigationVisible, canPeekNavigation, hasOpenNavigationOverlay, shouldNavigationYield, mainWindowMinWidth, settledBodyWidth, rowGrowthOnCanvasOpen } from './navigationVisibility'
import { usePeekExit } from './usePeekExit'
import { useRightSidebarMotion } from './useRightSidebarMotion'
import { useSidebarMenuBridge } from './useSidebarMenuBridge'
import { CompactListBar } from '../components/layout/CompactListBar'
import { UpdateSidebarOverlay } from '../components/layout/UpdateSidebarOverlay'
import { DoneMomentHost } from '../features/focus'
import { FocusOverlayHost } from './FocusOverlayHost'
import { useDockBadge } from '../hooks/useDockBadge'
import { usePendingOnboardingResponse } from '../hooks/usePendingOnboardingResponse'
import './MainLayout.scss'
import { getSectionTitle } from './sectionTitle'
import { breadcrumbTrail, projectOfPath } from './breadcrumbTrail'
import { useDataSource } from '../lib/dataSource'
import { staleProjectTabs } from './staleProjectTabs'

/*
  Jede Stufe macht beim Wechsel Platz bzw. klappt zusammen (`growMotion`, in der schnellen
  Taktung des Kontextmenüs): Was gleich bleibt, steht still, was dazukommt, blendet ein, was
  wegfällt, blendet aus (gewünscht 30.09.2026).

  **Tausch auf derselben Ebene** („TaskList Todos“ → „Geburtstags-Party“): Die alte Stufe
  verlässt sofort den Fluss und blendet an ihrem Platz aus (`popLayout`), die neue wächst an
  derselben Stelle heraus. Vorher wuchs sie NEBEN der alten und rutschte, sobald die zusammenklappte,
  um deren ganze Breite nach links. Stufen dahinter gleiten (`layout="position"`) statt zu springen.

  **Ausnahme: der Wechsel des Lebensbereichs** (gewünscht 07.10.2026). Dort wechselt die ganze
  Umgebung, nicht eine Ebene — wandernde und einwachsende Stufen wirkten wie ein Fehler. Die Stufen
  hängen dann unter einem `key` des Lebensbereichs neu ein (`BreadcrumbSteps`), ohne Animation.
*/
const breadcrumbStep = growMotion({ axis: 'width', size: 'auto', content: 0.2, enterDelay: 0.05, exitDelay: 0.1 })

/*
  **Ruhefenster nach einem Wechsel des Lebensbereichs.** Seitentitel (Effekt der Seite) und
  Elternprojekte (Datenbank-Abfrage) treffen einen Takt NACH dem Wechsel ein und gehören noch dazu —
  was in dieser Zeit neu kommt, steht ohne Einblenden da und ein wartender Wechsel der Stufen läuft
  sofort. Die Seiten melden nicht, wann sie fertig sind; ein Signal wäre ein Umbau über alle
  Listenseiten. Kommt ein Titel später (erster Besuch, lange Ladezeit), wächst er wie immer ein.
*/
const BREADCRUMB_QUIET_MS = 500
// Was im Ruhefenster wegfällt, geht ohne Ausblenden: Wer gleich nach dem Wechsel die Gruppe wechselt,
// sähe sonst die alte Beschriftung 0,2 s lang unter der neuen stehen (`popLayout` lässt sie an ihrer Stelle).
const breadcrumbStepInstantExit = { opacity: 0, transition: { duration: 0 } }

interface BreadcrumbStep {
  key: string
  text: string
  truncate: boolean
  /** Der Seitentitel — er schließt die Zeile ab und trägt kein Chevron dahinter. */
  title?: boolean
}

/**
 * Breadcrumb bar: life space › sidebar group, then the parent projects of a subproject (#185),
 * then the page title once it is provided via context. The deepest shown level is the current one.
 *
 * A project's title only shows while that project is open (`titleOwner`): on a switch the old
 * name must not stand behind the new levels, not even for the frame before the page clears it.
 *
 * **Zeile und `nav` bleiben beim Wechsel des Lebensbereichs stehen** — `MainLayout` beobachtet die
 * Zeile einmal (`--breadcrumb-height`); nur die Stufen hängen neu ein (`BreadcrumbSteps`).
 */
export const MainBreadcrumb: React.FC<{
  spaceName: string
  sectionTitle: string
  /** Parent projects of the open project, top first (`breadcrumbTrail`). */
  trail?: readonly string[]
  /** The open project, if any — a title owned by another project stays hidden. */
  activeProjectId?: string | null
  /** Der Lebensbereich — ein Wechsel hängt die Stufen ohne Animation neu ein. Rückfall: `spaceName`. */
  lifeSpace?: string
}> = ({ spaceName, sectionTitle, trail, activeProjectId, lifeSpace = spaceName }) => {
  // Erst ein ECHTER Wechsel löst das Ruhefenster aus, nicht schon der erste Aufbau der Zeile.
  const [shownSpace, setShownSpace] = useState(lifeSpace)
  const [switched, setSwitched] = useState(false)
  if (shownSpace !== lifeSpace) {
    setShownSpace(lifeSpace)
    setSwitched(true)
  }
  return (
    <div className="main-layout__breadcrumb-row">
      <Breadcrumb className="main-layout__breadcrumb">
        <BreadcrumbList>
          <BreadcrumbSteps
            key={lifeSpace}
            afterSwitch={switched}
            spaceName={spaceName}
            sectionTitle={sectionTitle}
            trail={trail}
            activeProjectId={activeProjectId}
          />
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  )
}

const BreadcrumbSteps: React.FC<{
  /** Der Baustein hängt nach einem Wechsel des Lebensbereichs neu ein — dann gilt das Ruhefenster. */
  afterSwitch: boolean
  spaceName: string
  sectionTitle: string
  trail?: readonly string[]
  activeProjectId?: string | null
}> = ({ afterSwitch, spaceName, sectionTitle, trail = [], activeProjectId = null }) => {
  const { title: rawTitle, titleOwner } = useBreadcrumbTitle()

  // `arriving`: der erste Aufbau — im Kontext steht noch der Titel der ALTEN Seite (sie räumt ihn
  // erst im Effekt ab), er bliebe sichtbar stehen und führe dann hinaus. Danach `quiet`, dann `live`.
  const [phase, setPhase] = useState<'arriving' | 'quiet' | 'live'>(afterSwitch ? 'arriving' : 'live')
  useEffect(() => {
    if (!afterSwitch) return
    setPhase('quiet')
    const id = setTimeout(() => setPhase('live'), BREADCRUMB_QUIET_MS)
    return () => clearTimeout(id)
  }, [afterSwitch])

  const title = phase === 'arriving' ? null : titleOwner === null || titleOwner === activeProjectId ? rawTitle : null
  const navSteps: BreadcrumbStep[] = [
    { key: `space:${spaceName}`, text: spaceName, truncate: false },
    // Kurz notiert hat keine Gruppe (`getSectionTitle`) — dann keine leere Stufe.
    ...(sectionTitle ? [{ key: `section:${sectionTitle}`, text: sectionTitle, truncate: false }] : []),
    ...trail.map((name, i) => ({ key: `trail:${i}:${name}`, text: name, truncate: true })),
  ]
  const navKey = navSteps.map(step => step.key).join('|')

  /*
    **Erst wenn die Seite steht** (gewünscht 30.09.2026): Ein Wechsel von Bereich, Gruppe oder
    Elternprojekten fällt mitten in die Arbeit der neuen Seite (laden, rendern, überblenden). Dort
    fielen Bilder der Einblendung weg, und die neue Stufe wirkte gehetzt. Deshalb wartet der
    Wechsel auf den Leerlauf (`whenCalm`, höchstens 250 ms) und läuft dann in voller Länge. Bis
    dahin bleiben die alten Stufen stehen — ohne Titel, der gehört dem alten Projekt. Der Titel
    beim Scrollen wartet nicht.

    Im Ruhefenster nach einem Wechsel des Lebensbereichs wartet nichts: dort läuft keine Animation,
    auf deren Bilder man Rücksicht nähme — Neues erscheint und Altes geht sofort.
  */
  const [settledNav, setSettledNav] = useState({ key: navKey, steps: navSteps })
  const latestNavRef = useRef({ key: navKey, steps: navSteps })
  latestNavRef.current = { key: navKey, steps: navSteps }
  const pending = phase === 'live' && settledNav.key !== navKey
  useEffect(() => {
    if (phase !== 'live') {
      setSettledNav(prev => (prev.key === latestNavRef.current.key ? prev : latestNavRef.current))
      return
    }
    if (!pending) return
    return whenCalm(() => setSettledNav(latestNavRef.current))
  }, [phase, pending, navKey])

  const steps = pending
    ? settledNav.steps
    : [...navSteps, ...(title ? [{ key: `title:${title}`, text: title, truncate: false, title: true }] : [])]
  const last = steps.length - 1
  // Nur ein Wechsel der Stufen löst die Layout-Animation aus — nicht jedes Neu-Rendern: Wandert
  // die ganze Zeile (Pinnwand öffnet sich), zögen die Stufen sonst hinterher.
  const stepsKey = steps.map(step => step.key).join('|')
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {steps.map((step, i) => (
        <motion.li
          key={step.key}
          className="breadcrumb__item main-layout__breadcrumb-step"
          layout="position"
          layoutDependency={stepsKey}
          initial={phase === 'live' ? breadcrumbStep.initial : false}
          animate={breadcrumbStep.animate}
          exit={phase === 'live' ? breadcrumbStep.exit : breadcrumbStepInstantExit}
          style={breadcrumbStep.style}
        >
          {/* Das Chevron steht HINTER der Stufe, auch hinter der letzten („Arbeit ›“,
              gewünscht 06.10.2026) — nur nicht hinter dem Seitentitel, der schließt ab
              („Arbeit › Heute & Danach › Geschafft“). So bringt eine neue Stufe ihr eigenes
              mit und wächst als Ganzes herein; die Stufen davor bleiben stehen. */}
          <span className="main-layout__breadcrumb-step-inner">
            <BreadcrumbPage
              className={`${step.truncate ? 'breadcrumb__page--truncate' : ''}${i === last && i > 0 ? ' breadcrumb__page--current' : ''}`}
            >
              {step.text}
            </BreadcrumbPage>
            {!step.title && (
              <span className="breadcrumb__separator" aria-hidden="true">
                <ChevronRight size={14} />
              </span>
            )}
          </span>
        </motion.li>
      ))}
    </AnimatePresence>
  )
}

interface MainLayoutProps {
  children: React.ReactNode
}

/** Titel und Icon der Seiten, auf die ein Treffer der Suche führt — wie ihre Einträge in der Sidebar. */
const SEARCH_PAGE_TABS: Record<SearchPage, { titleKey: string; icon: LucideIcon }> = {
  'quick-notes': { titleKey: 'sidebar.quickNotes', icon: QuickNotesFilledIcon as unknown as LucideIcon },
  upcoming: { titleKey: 'sidebar.upcoming', icon: UpcomingIcon as unknown as LucideIcon },
  dormant: { titleKey: 'sidebar.dormant', icon: DormantIcon as unknown as LucideIcon },
  recurring: { titleKey: 'sidebar.recurring', icon: RecurringIcon as unknown as LucideIcon },
  goals: { titleKey: 'sidebar.goals', icon: GoalsIcon as unknown as LucideIcon },
  done: { titleKey: 'sidebar.done', icon: CircleCheckBig },
}

const MainLayoutComponent: React.FC<MainLayoutProps> = ({ children }) => {
  const { t, i18n } = useTranslation()
  // Unteraufgaben verlieren ihren Rhythmus, wenn ihre Aufgabe wiederkehrend wird (#205) — einmal je Hauptfenster.
  useChildRecurrenceNotice()
  const { activeLifeSpace, setActiveLifeSpace, spaces } = useLifeSpaces()

  // Refs for latest values — read in handleLifeSpaceChange to avoid stale closures
  const activeSpaceRef = useRef(activeLifeSpace)
  activeSpaceRef.current = activeLifeSpace

  // Die Zahl auf dem Dock-Symbol folgt „Demnächst" im aktiven Bereich.
  useDockBadge(activeLifeSpace)
  // Frisch aus den Folien: Beispielprojekt und Angebot der Einführung (#323), einmal je Gerät.
  useIntroStart(activeLifeSpace)
  const noteIntro = useIntroNote()
  const spacesRef = useRef(spaces)
  spacesRef.current = spaces
  const { uiState, updateUiState } = useWindowState()
  const { isElectron, isMac } = usePlatform()
  const preferencesDialog = usePreferencesDialog()
  // `showTopFade` / `showBottomFade` are no longer read — MainLayout's
  // scroll-wrap stopped scrolling when TabStack took over per-slot scrolling,
  // so they never fire. Fades are rendered per TabStack slot now.
  const { containerRef: scrollRef, scrollbarVisible, onScroll: handleContentScroll } = useAutoScrollbar()
  const {
    tabs,
    activeTab,
    isSecondaryWindow,
    secondaryReady,
    switchToSpace,
    openTab,
    closeTab,
    setActiveTab,
    pinTab,
    closeOtherTabs,
    reorderTabs,
    navigateActiveTab,
    openPreviewTab,
    promotePreviewTab,
    promotePreviewBySpace,
    updateTab,
    showPage,
    getDefaultTabForLifeSpace
  } = useTabManager()
  // Erinnerungen zustellen (Plan task-reminders) — nur im ersten Hauptfenster, abgerissene Tabs schweigen.
  useReminderDelivery({ openTab, primary: !isSecondaryWindow })

  // Die Elternprojekte des offenen Projekts für die Breadcrumb (#185) — aus der Adresse des Tabs.
  // Die Projekte des Bereichs bleiben hier dauerhaft geladen, damit ein Wechsel die Stufen in
  // einem Schritt tauscht und keine Stufe, die bleibt, kurz verschwindet.
  const breadcrumbProject = projectOfPath(activeTab?.path)
  const breadcrumbProjects = useDataSource().useProjectsByLifeSpace(
    breadcrumbProject?.lifeSpace ?? (activeLifeSpace === 'all' ? 'personal' : activeLifeSpace),
  )
  const breadcrumbLevels = useMemo(
    () => breadcrumbTrail(activeTab?.path, breadcrumbProjects ?? [], t('project.defaultName')),
    [activeTab?.path, breadcrumbProjects, t],
  )

  // Per-tab scroll state is now preserved automatically by TabStack — each
  // slot is its own scroll container and stays mounted when inactive, so the
  // DOM retains `scrollTop` across switches. The previous save/restore logic
  // keyed off MainLayout's scroll-wrap, which no longer scrolls (content
  // fits exactly) and was always saving/restoring 0.

  // NOTE: Default tab opening is handled in useTabManager hook
  // to avoid conflicts with session restoration

  // Sync project names into ALL open project tabs (not just the active one).
  // Covers cross-device renames arriving via sync.
  // IMPORTANT: Read `tabs` via ref inside the effect to avoid an infinite loop
  // (updateTab changes tabs → would re-trigger the effect if tabs were a dependency).
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs

  // Stable projectTabIds — only change reference when the actual IDs change
  const projectTabIdsRef = useRef<string[]>([])
  const projectTabIds = useMemo(() => {
    const ids: string[] = []
    for (const tab of tabs) {
      const match = tab.path.match(/\/project\/([^/]+)/)
      if (match) ids.push(match[1])
    }
    const prev = projectTabIdsRef.current
    if (ids.length === prev.length && ids.every((id, i) => id === prev[i])) {
      return prev
    }
    projectTabIdsRef.current = ids
    return ids
  }, [tabs])

  // `anyOf([])` already resolves to an empty array, so no ternary/`Promise.resolve`
  // fallback is needed — and the fallback's `never[]` type broke `projectData`.
  // Mit den gefragten IDs zusammen, damit `staleProjectTabs` Ergebnisse zu alten IDs erkennt.
  const projectLookup = useLiveQuery(
    async () => ({ ids: projectTabIds, rows: await db.projects.where('id').anyOf(projectTabIds).toArray() }),
    [projectTabIds]
  )
  const projectData = projectLookup?.rows

  useEffect(() => {
    if (!projectData || projectData.length === 0) return
    const nameById = new Map(projectData.map(p => [p.id, p.name]))
    for (const tab of tabsRef.current) {
      const match = tab.path.match(/\/project\/([^/]+)/)
      if (!match) continue
      const projectName = nameById.get(match[1])
      if (projectName !== undefined && projectName !== tab.title) {
        updateTab(tab.id, { title: projectName || t('project.defaultName') })
      }
    }
  }, [projectData, updateTab, t])

  // Tabs von Projekten, die es nicht mehr gibt (per Sync gelöscht, Datenbank
  // geleert), schließen. Einer je Lauf: `closeTab` liest die Tabs aus einem Ref,
  // der erst nach dem Rendern nachzieht — der nächste folgt mit der neuen Abfrage.
  useEffect(() => {
    const [stale] = staleProjectTabs(tabsRef.current, projectLookup)
    if (!stale) return
    if (tabsRef.current.length > 1) {
      closeTab(stale)
      return
    }
    const fallback = getDefaultTabForLifeSpace(stale.lifeSpace || 'personal')
    if (fallback) navigateActiveTab({ path: fallback.path, title: fallback.title, icon: fallback.icon })
  }, [projectLookup, closeTab, getDefaultTabForLifeSpace, navigateActiveTab])

  // ── Sichtbarkeit der Navigation ──
  // Zwei Zustände, keine Icon-Leiste dazwischen (ADR 0005). Ein offener
  // Projekt-Canvas blendet sie zusätzlich aus — bewusst NICHT über
  // `updateUiState({ sidebarLeft: false })`: der Wert wird per IPC persistiert,
  // die Navigation bliebe also auch nach dem Schließen des Canvas weg, obwohl
  // der User sie nie ausgeblendet hat. Stattdessen legt sich ein CSS-Modifier
  // darüber, was das Wiederherstellen gratis macht — der gespeicherte Wert gilt
  // einfach wieder.
  const { isCanvasFocused, canvasMode } = useCanvasMode(activeTab?.id ?? null)
  // Die **Aufgaben-Details** beanspruchen die rechte Hälfte wie ein geteilter
  // Canvas — und wie ihm weicht die Breadcrumb (siehe MainLayout.scss).
  const detailsPanelOpen = useDetailsPanelOpen(activeTab?.id ?? null)
  // Was die Liste der Seite daneben braucht — Demnächst mehr als eine Aufgabenliste (#226).
  const detailsListMinWidth = useDetailsListMinWidth(activeTab?.id ?? null)
  // Was die Liste der Seite immer braucht — Demnächst mehr als eine Aufgabenliste (#296).
  const pageListMinWidth = usePageListMinWidth(activeTab?.id ?? null)

  // Tatsächliche Höhe der Breadcrumb-Zeile als CSS-Variable weiterreichen.
  //
  // Bei offenem Canvas verlässt die Zeile den Fluss, und die Projektseite muss
  // den entfallenen Platz exakt ausgleichen — sonst rückt die Aufgabenliste
  // näher an die Breadcrumb als im geschlossenen Zustand. Der Wert war zuerst
  // mit 40px geschätzt (10px Polsterung + Zeile + 10px), aber die Zeile hat
  // keine feste `line-height`: ihre Höhe ergibt sich aus Schriftgröße und
  // Trennsymbol und ändert sich mit beidem. Messen statt rechnen.
  useEffect(() => {
    const content = contentRef.current
    const row = content?.querySelector<HTMLElement>('.main-layout__breadcrumb-row')
    if (!content || !row) return

    const apply = () => {
      const height = row.offsetHeight
      if (height > 0) content.style.setProperty('--breadcrumb-height', `${height}px`)
    }

    const observer = new ResizeObserver(apply)
    observer.observe(row)
    apply()
    return () => observer.disconnect()
  }, [])
  /*
    **Die Navigation weicht den Aufgaben-Details und der offenen Pinnwand, wenn es
    sonst nicht reicht** (Abnahme 21.09.2026, F-Q3; Pinnwand seit #296, maximiert seit #297). Gemessen
    wird der Körper, nicht das Fenster: Er trägt die Navigation UND den Inhalt,
    und nur seine Breite entscheidet, ob Liste und Karte bzw. Canvas
    nebeneinander passen. Die Regel samt ihren Maßen steht in
    `navigationVisibility.ts`.
  */
  const bodyRef = useRef<HTMLDivElement>(null)
  const [bodyWidth, setBodyWidth] = useState<number | null>(null)
  useEffect(() => {
    const body = bodyRef.current
    if (!body) return
    const apply = () => setBodyWidth(body.clientWidth || null)
    const observer = new ResizeObserver(apply)
    observer.observe(body)
    apply()
    return () => observer.disconnect()
  }, [])

  // Window mode (compact "Liste"/"Stapel"): hide chrome + drive the real window resize via the WindowModeProvider.
  // Steht vor dem Weichen der Navigation: Im kompakten Fenster und auf dem Weg zurück weicht sie nicht
  // (`settledBodyWidth`).
  const {
    isStackMode, isCompactMode,
    toggleListMode, toggleStackMode, switchToListMode, switchToStackMode,
    switchToFullMode, alwaysOnTop, toggleAlwaysOnTop,
  } = useWindowMode()

  const yieldInput = {
    detailsOpen: detailsPanelOpen,
    bodyWidth: settledBodyWidth({ bodyWidth, compact: isCompactMode, isElectron }),
    listMinWidth: detailsListMinWidth,
    pageListMinWidth,
  }
  const navYields = shouldNavigationYield({ ...yieldInput, canvasOpen: isCanvasFocused })
  const navState = { sidebarLeft: uiState.sidebarLeft, yieldToDetails: navYields }

  const isNavVisible = isNavigationVisible(navState)

  /*
    Die Pinnwand friert beim Start ihrer Bewegung den Inhalt auf die Breite ein, die sie am Ende haben wird. Weicht
    die Navigation dabei oder kommt sie zurück, ändert die Zeile neben ihr noch während der Bewegung ihre Breite —
    das muss sie vorher wissen (#297).
    Ihre Meldung kommt erst nach ihrer eigenen Messung hier an; darum rechnet sie mit dem, was hier hinterlegt ist.
  */
  const { setRowGrowthForecast } = useCanvasFocus()
  const rowGrowth = rowGrowthOnCanvasOpen({ sidebarLeft: uiState.sidebarLeft, ...yieldInput, canvasOpen: isCanvasFocused })
  useLayoutEffect(() => {
    setRowGrowthForecast(() => rowGrowth)
  }, [setRowGrowthForecast, rowGrowth])

  // Die rechte Sidebar gibt es nur noch auf „Ziele". `available` steuert, ob
  // Icon und Shortcut überhaupt etwas zu tun haben; `visible` zusätzlich die
  // gespeicherte Wahl des Users, die anderswo erhalten bleibt und beim
  // Zurückkommen wieder gilt.
  const rightSidebarAvailable = isGoalsPath(activeTab?.path)
  const rightSidebarVisible = isRightSidebarVisible({
    activeTabPath: activeTab?.path,
    sidebarRight: uiState.sidebarRight,
  })
  // Während des Umschaltens zieht die Karte ihren rechten Rand mit (#277) — nicht beim Tab-Wechsel.
  const rightSidebarMotion = useRightSidebarMotion(rightSidebarVisible, rightSidebarAvailable)

  // ── Hover-Peek ──
  // Hovering the titlebar's sidebar icon slides the navigation in as an
  // overlay — it floats above the content instead of pushing it right.
  // Only useful where the navigation isn't already shown.
  const [isSidebarPeeking, setIsSidebarPeeking] = useState(false)
  const peekCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navRef = useRef<HTMLDivElement>(null)
  const canPeek = canPeekNavigation(navState)

  const cancelPeekClose = useCallback(() => {
    if (peekCloseTimer.current) {
      clearTimeout(peekCloseTimer.current)
      peekCloseTimer.current = null
    }
  }, [])

  const openPeek = useCallback(() => {
    cancelPeekClose()
    setIsSidebarPeeking(true)
  }, [cancelPeekClose])

  // Verzögert, damit der Zeiger die Lücke zwischen Icon und Panel überqueren
  // kann, ohne dass der Peek darunter wegfällt.
  //
  // Steht dabei ein Menü offen, das aus der Navigation heraus geöffnet wurde
  // (etwa ein Kontextmenü), wird der Timer NEU GESTELLT statt
  // abgebrochen: diese Panels hängen per Portal an `document.body`, der Zeiger
  // löst auf dem Weg dorthin also `mouseleave` aus — und ein blosses `return`
  // hier ließe den Peek für immer offen, weil danach kein weiteres
  // `mouseleave` mehr kommt. Der sanfte Puls läuft nur während eines
  // schließenden Peeks und endet von allein, sobald das Menü verschwindet.
  // Steht der Zeiger bis dahin wieder über der Leiste, bleibt sie offen —
  // dasselbe, was ihr `mouseenter` sonst täte.
  const closePeek = useCallback(() => {
    cancelPeekClose()
    const tick = () => {
      if (hasOpenNavigationOverlay()) {
        peekCloseTimer.current = setTimeout(tick, 150)
        return
      }
      if (navRef.current?.matches(':hover')) return
      setIsSidebarPeeking(false)
    }
    peekCloseTimer.current = setTimeout(tick, 150)
  }, [cancelPeekClose])

  useEffect(() => () => cancelPeekClose(), [cancelPeekClose])

  // A peek over an already-open sidebar would be a no-op that still steals the
  // layout; drop it as soon as peeking stops making sense.
  useEffect(() => {
    if (!canPeek && isSidebarPeeking) setIsSidebarPeeking(false)
  }, [canPeek, isSidebarPeeking])

  const isPeekActive = isSidebarPeeking && canPeek

  // Hält den Peek offen, solange der Zeiger über der Leiste steht. NATIVE
  // Listener statt `onMouseEnter`/`onMouseLeave`: React bildet diese Ereignisse
  // am React-Baum nach, und dort gilt ein Portal-Menü (etwa ein Kontextmenü)
  // als Kind der Leiste. Wechselte der Zeiger hinein, kam kein `mouseleave` —
  // und nach dem Schließen des Menüs auch keines mehr: die Leiste blieb stehen
  // (bis 14.09.2026, `.claude/tasks/peek-stays-open-after-menu.md`).
  useEffect(() => {
    const nav = navRef.current
    if (!isPeekActive || !nav) return
    nav.addEventListener('mouseenter', cancelPeekClose)
    nav.addEventListener('mouseleave', closePeek)
    return () => {
      nav.removeEventListener('mouseenter', cancelPeekClose)
      nav.removeEventListener('mouseleave', closePeek)
    }
  }, [isPeekActive, cancelPeekClose, closePeek])
  // Der Peek behält seine Overlay-Geometrie, bis er ausgefahren ist — sonst
  // kämen die Leisten mitten in der Breiten-Animation in den Flex-Fluss zurück
  // und schöben den Inhalt sichtbar hin und her. Beim Festsetzen per Klick gilt
  // das Gegenteil: dort muss der Fluss sofort greifen.
  const peekExit = usePeekExit(isPeekActive, isNavVisible)

  // Memoized callbacks for sidebar toggles - MUST be defined BEFORE useEffect
  const toggleLeftSidebar = useCallback(() => {
    updateUiState({ sidebarLeft: !uiState.sidebarLeft })
  }, [uiState.sidebarLeft, updateUiState])

  const toggleRightSidebar = useCallback(() => {
    // Außerhalb von „Ziele" gibt es kein Panel — ein Umschalten würde dort
    // unsichtbar am gespeicherten Wert drehen.
    if (!rightSidebarAvailable) return
    updateUiState({ sidebarRight: !uiState.sidebarRight })
  }, [rightSidebarAvailable, uiState.sidebarRight, updateUiState])

  // Register sidebar toggles in TitlebarContext so Titlebar buttons can call them
  const { pageHistoryRef, setPageHistory, sidebarToggleRef, rightSidebarToggleRef, sidebarPeekEnterRef, sidebarPeekLeaveRef, exitToFullRef, setSidebarLeftOpen, setSidebarRightOpen, setRightSidebarAvailable, setMainLayoutActive, tabBarSlotRef, tabBarSlotReady, unionShapeSlotRef, unionShapeSlotReady, bumpSlotRef, bumpSlotReady } = useTitlebarContext()

  useEffect(() => {
    sidebarToggleRef.current = toggleLeftSidebar
    rightSidebarToggleRef.current = toggleRightSidebar
    sidebarPeekEnterRef.current = openPeek
    sidebarPeekLeaveRef.current = closePeek
    return () => {
      sidebarToggleRef.current = null
      rightSidebarToggleRef.current = null
      sidebarPeekEnterRef.current = null
      sidebarPeekLeaveRef.current = null
    }
  }, [toggleLeftSidebar, toggleRightSidebar, sidebarToggleRef, rightSidebarToggleRef, openPeek, closePeek, sidebarPeekEnterRef, sidebarPeekLeaveRef])

  useSidebarMenuBridge(toggleLeftSidebar, isNavVisible)

  // Signal to Titlebar that MainLayout is active (for web route-aware app controls)
  useEffect(() => {
    setMainLayoutActive(true)
    return () => setMainLayoutActive(false)
  }, [setMainLayoutActive])

  useQuickAddPrewarm()
  // Wo die Aufgabe aus der Schnelleingabe gelandet ist — mit Sprung dorthin; nach ⇧↵ gleich der Sprung (Sven, 04.10.2026).
  useQuickAddCreatedToast()
  useQuickAddReveal()

  // Track app open for retention analytics
  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.APP_OPENED, {
      platform: isElectron ? 'electron' : 'web',
    })
  }, [isElectron])

  // Sync sidebar open state to TitlebarContext for icon switching.
  // The EFFECTIVE state, not the persisted one: in canvas focus the navigation
  // is gone, so the titlebar has to pull the tab left with it. Feeding
  // `uiState.sidebarLeft` here would leave the tab parked while the sidebar
  // slid out from under it.
  //
  // WICHTIG — `useLayoutEffect`, nicht `useEffect`. Die Titlebar hängt nicht am
  // JSX von MainLayout, sondern steht darüber im Baum; ihr Zustand erreicht sie
  // nur über diesen Umweg durch den Context. Ein Effekt nach dem Malen setzte
  // ihn erst im nächsten Frame — die Leisten und die Inhaltskarte sprängen also
  // sofort an ihren Platz, die Tabs samt Beschriftung erst einen Frame später.
  // Genau als solches Nachspringen war das sichtbar. Ein Layout-Effekt kommt vor
  // dem Malen und rendert die Titlebar noch im selben Frame neu.
  useLayoutEffect(() => {
    setSidebarLeftOpen(isNavVisible)
  }, [isNavVisible, setSidebarLeftOpen])

  useLayoutEffect(() => {
    setSidebarRightOpen(rightSidebarVisible)
  }, [rightSidebarVisible, setSidebarRightOpen])

  // Ohne dieses Signal bliebe der Umschalt-Knopf in der Titlebar überall stehen,
  // auch wo er nichts bewirkt. Ebenfalls als Layout-Effekt: sein Erscheinen und
  // Verschwinden ändert die Breite der rechten Bedienelemente.
  useLayoutEffect(() => {
    setRightSidebarAvailable(rightSidebarAvailable)
  }, [rightSidebarAvailable, setRightSidebarAvailable])

  // Sort tabs the same way TabBar does (pinned first) for accurate first-tab detection
  const sortedTabs = useMemo(() =>
    tabs.toSorted((a, b) => {
      if (a.isPinned && !b.isPinned) return -1
      if (!a.isPinned && b.isPinned) return 1
      return 0
    }),
    [tabs]
  )

  // Create a new project in the active life space
  const handleCreateProject = useCallback(async () => {
    const lifeSpace = activeLifeSpace as 'personal' | 'work' | 'home'
    if (!['personal', 'work', 'home'].includes(lifeSpace)) return

    const project = await db.createProject({
      name: '',
      description: null,
      color: 'var(--space-500)',
      status: 'active',
      lifeSpace,
      startDate: null,
      endDate: null,
      progress: 0,
    })
    noteIntro('projects')
    navigateActiveTab({
      path: `/home/${lifeSpace}/project/${project.id}`,
      title: t('project.defaultName'),
      icon: AreaIcon as any,
    })
  }, [activeLifeSpace, navigateActiveTab, t, noteIntro])

  // Refs for volatile values — read inside keydown handler to avoid re-registration
  const activeTabRef = useRef(activeTab)
  activeTabRef.current = activeTab
  const sortedTabsRef = useRef(sortedTabs)
  sortedTabsRef.current = sortedTabs
  const uiStateRef = useRef(uiState)
  uiStateRef.current = uiState

  // Keyboard shortcuts für Sidebar Toggles, Tab Navigation und Quick Add
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey

      // Toggle left sidebar
      if (isMod && e.key === 'b' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        toggleLeftSidebar()
      }

      // Toggle right sidebar
      if (isMod && e.shiftKey && e.key === 'b' && !e.altKey) {
        e.preventDefault()
        toggleRightSidebar()
      }

      // Toggle both sidebars (Cmd+Option+B / Ctrl+Alt+B)
      // Use e.code because Option/Alt changes e.key on macOS (e.g. Option+B → '∫')
      if (isMod && e.altKey && e.code === 'KeyB') {
        e.preventDefault()
        const ui = uiStateRef.current
        const bothOpen = ui.sidebarLeft && ui.sidebarRight
        updateUiState({ sidebarLeft: !bothOpen, sidebarRight: !bothOpen })
      }

      // Skip arrow-key shortcuts when focus is inside a text field
      const target = e.target as HTMLElement
      const isEditing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

      // ⌘← / ⌘→ gehören dem Seitenverlauf — eigener Block weiter unten (#225).

      // Switch tab left (Cmd+Option+Left / Ctrl+Alt+Left)
      if (isMod && e.altKey && e.code === 'ArrowLeft') {
        if (isEditing) return
        e.preventDefault()
        const st = sortedTabsRef.current
        const at = activeTabRef.current
        if (st.length > 1 && at) {
          const currentIndex = st.findIndex(t => t.id === at.id)
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : st.length - 1
          setActiveTab(st[prevIndex])
        }
      }

      // Switch tab right (Cmd+Option+Right / Ctrl+Alt+Right)
      if (isMod && e.altKey && e.code === 'ArrowRight') {
        if (isEditing) return
        e.preventDefault()
        const st = sortedTabsRef.current
        const at = activeTabRef.current
        if (st.length > 1 && at) {
          const currentIndex = st.findIndex(t => t.id === at.id)
          const nextIndex = currentIndex < st.length - 1 ? currentIndex + 1 : 0
          setActiveTab(st[nextIndex])
        }
      }

      // Life Space cycling (Cmd/Ctrl+Option+Up/Down) is handled in LifeSpacesSidebar
      // alongside the L shortcut — same variables, same callback, guaranteed consistency.


      /*
        **`P` gehört seit dem 16.09.2026 dem Fokus** („**P**lay",
        `useTaskMenuShortcuts`). Es hing bis dahin hier und heftete den aktiven
        Tab an — Play ist wichtiger, und angeheftet wird weiter über den Knopf
        am Tab, den Doppelklick und das Kontextmenü.

        Hier wäre es ohnehin die falsche Stelle: `MainLayout` ist der Rahmen und
        hat kein `isActive`, es finge die Taste also auch aus inaktiven Tabs ab.
      */

      // Close other tabs (Cmd+Shift+W / Ctrl+Shift+W)
      if (isMod && e.shiftKey && e.code === 'KeyW') {
        e.preventDefault()
        const at = activeTabRef.current
        if (at) closeOtherTabs(at)
      }

      // Create new project (Alt+Cmd+N / Alt+Ctrl+N)
      // Use e.code because Option/Alt changes e.key on macOS
      if (isMod && e.altKey && e.code === 'KeyN') {
        e.preventDefault()
        handleCreateProject()
      }

      // Cmd/Ctrl+N is handled by ProjectPage for inline task creation

      // Open preferences (Cmd+, / Ctrl+,)
      // On macOS the OS app menu accelerator (menuService.ts) intercepts this
      // before it reaches the renderer; this branch covers Windows/Linux and
      // the web build, where no native menu is registered.
      if (isMod && !e.altKey && !e.shiftKey && e.code === 'Comma') {
        e.preventDefault()
        if (window.api?.invoke) {
          window.api.invoke('preferences:open')
        } else if (preferencesDialog) {
          preferencesDialog.open()
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [toggleLeftSidebar, toggleRightSidebar, updateUiState, setActiveTab, closeOtherTabs, handleCreateProject, preferencesDialog])

  // IPC: close active tab (triggered by Cmd+W via main process menu)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('close-active-tab', () => {
      if (!activeTab) return
      if (tabs.length > 1) {
        closeTab(activeTab)
      } else if (window.api?.invoke) {
        // Den letzten Tab kann man nicht schließen — ⌘W schließt dann das Fenster.
        // Im Hauptfenster wirkt das wie das X (macOS/Tray: nur verstecken).
        window.api.invoke('window-close')
      }
    })
    return () => {
      unsubscribe?.()
    }
  }, [activeTab, tabs.length, closeTab])

  // IPC: Ablage › „Neue wiederkehrende Aufgabe" — Seite im aktiven Lifespace
  // öffnen (vorhandenen Tab wiederverwenden), die Seite öffnet dann den Anleger.
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('create-new-recurring-task', () => {
      const path = `/home/${activeLifeSpace}/recurring`
      openTab({
        id: `${activeLifeSpace}-recurring-${Date.now()}`,
        path,
        title: t('sidebar.recurring'),
        icon: RecurringIcon,
        lifeSpace: activeLifeSpace,
        isDirty: false,
        isPinned: false,
      })
      requestRecurringCreate()
    })
    return () => {
      unsubscribe?.()
    }
  }, [activeLifeSpace, openTab, t])

  // IPC: Hilfe › Feedback geben … — derselbe Weg wie in der Titlebar, die ihn im schmalen Fenster ausblendet (#296).
  useEffect(() => {
    if (!window.api?.receive) return
    return window.api.receive('menu:feedback', () => openFeedbackForm())
  }, [])

  // IPC: Hilfe › Alle Toasts zeigen — das Menü bietet ihn nur in der Entwicklung an (#223).
  useEffect(() => {
    if (!window.api?.receive) return
    return window.api.receive('dev:show-all-toasts', () => showAllToastsForDev(t, i18n.language ?? 'de'))
  }, [t, i18n.language])

  // IPC: **Testdaten** (#269) — Hilfe › Testdaten anlegen / entfernen …, dazu `npm run testdata`.
  const testDataDialog = useTestDataMenuBridge()

  // IPC: create new project (triggered by File > New Project menu)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('create-new-project', () => {
      handleCreateProject()
    })
    return () => {
      unsubscribe?.()
    }
  }, [handleCreateProject])

  // Secondary window: close when all tabs are gone (e.g. last tab closed via X button)
  // Wait for secondaryReady to avoid closing before init data is pulled
  useEffect(() => {
    if (isSecondaryWindow && secondaryReady && tabs.length === 0 && window.api?.invoke) {
      window.api.invoke('window-close')
    }
  }, [isSecondaryWindow, secondaryReady, tabs.length])


  // Liegengebliebene Setup-Antworten einmal nachsenden (anonym, 25.09.2026).
  usePendingOnboardingResponse(!isSecondaryWindow)

  // Life space change: update active space and switch tabs.
  const handleLifeSpaceChange = useCallback((spaceId: string) => {
    // Nur der Wechsel von Hand zählt für „Entdecken“ (#323), nicht ein Projekt, das den Bereich mitnimmt.
    if (spaceId !== activeSpaceRef.current) noteIntro('lifespaces')
    setActiveLifeSpace(spaceId)
    switchToSpace(spaceId)
  }, [setActiveLifeSpace, switchToSpace, noteIntro])

  /*
    **Ein Projekt zieht in einen anderen Lebensbereich** (#220): Zeigt der aktive Tab es, gehen Tab
    und App mit. Derselbe Weg wie der Sprung über den Lebensbereich im Projekt-Umschalter
    (`CompactListBar`): `navigateActiveTab` mit `lifeSpace` setzt den Tab um, `setActiveLifeSpace`
    die App. `switchToSpace` wäre falsch — es holte den zuletzt benutzten Tab des Ziels nach vorn.
  */
  useEffect(() => onAppEvent('project-moved-life-space', ({ projectId, lifeSpace }) => {
    const tab = activeTabRef.current
    if (!tab || tab.path.match(/\/project\/([^/?#]+)/)?.[1] !== projectId) return
    navigateActiveTab({ path: `/home/${lifeSpace}/project/${projectId}`, title: tab.title, icon: tab.icon, lifeSpace })
    setActiveLifeSpace(lifeSpace)
  }), [navigateActiveTab, setActiveLifeSpace])


  // First tab uses simple corner (no left S-curve) unless on macOS Electron
  // with sidebar collapsed, where the search icon provides space for the S-curve
  const isFirstTabActive = sortedTabs.length > 0 && sortedTabs[0].id === activeTab?.id
    && (isElectron ? (!isMac || uiState.sidebarLeft) : true)

  // Track pinned tab count so ContentUnionShape re-measures when pin order changes
  const pinnedCount = useMemo(() => tabs.filter(t => t.isPinned).length, [tabs])

  // Track max width for TabBar so it never exceeds the content area.
  // Uses direct DOM manipulation (no React state) to avoid re-render jitter
  // during sidebar CSS transitions.
  const contentRef = useRef<HTMLDivElement>(null)
  const tabBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const contentEl = contentRef.current
    const slotEl = tabBarSlotRef.current
    if (!contentEl) return

    const update = () => {
      const barEl = tabBarRef.current
      if (!barEl) return
      if (slotEl) {
        const contentRect = contentEl.getBoundingClientRect()
        const slotRect = slotEl.getBoundingClientRect()
        barEl.style.maxWidth = `${Math.max(0, Math.round(contentRect.right - slotRect.left))}px`
      } else {
        barEl.style.maxWidth = `${contentEl.offsetWidth}px`
      }
    }

    const observer = new ResizeObserver(update)
    observer.observe(contentEl)
    if (slotEl) observer.observe(slotEl)
    update()
    return () => observer.disconnect()
  }, [tabBarSlotRef])

  // Close a tab by its path (used by sidebar close-tab buttons)
  const closeTabByPath = useCallback((path: string) => {
    const tab = tabs.find(t => t.path === path)
    if (tab) closeTab(tab)
  }, [tabs, closeTab])

  // Close the currently active tab (used by page components when their content is deleted)
  const closeActiveTab = useCallback(() => {
    if (!activeTab) return
    if (tabs.length > 1) {
      closeTab(activeTab)
    } else {
      // Last tab — navigate to default page for this life space
      const defaultTab = getDefaultTabForLifeSpace(activeTab.lifeSpace || 'personal')
      if (defaultTab) {
        navigateActiveTab({ path: defaultTab.path, title: defaultTab.title, icon: defaultTab.icon })
      }
    }
  }, [activeTab, tabs.length, closeTab, getDefaultTabForLifeSpace, navigateActiveTab])

  // Sidebar hover → tab highlight: event delegation on sidebar [data-path] elements
  useEffect(() => {
    const sidebar = document.querySelector('.standard-sidebar')
    if (!sidebar) return

    let lastHighlighted: Element | null = null

    const handleOver = (e: Event) => {
      const target = (e.target as HTMLElement).closest('[data-path]')
      const path = target?.getAttribute('data-path')

      if (lastHighlighted) {
        lastHighlighted.classList.remove('tab-bar__tab--sidebar-hover')
        lastHighlighted = null
      }

      if (path) {
        const tabEl = document.querySelector(`.tab-bar__tab[data-path="${CSS.escape(path)}"]:not(.tab-bar__tab--active)`)
        if (tabEl) {
          tabEl.classList.add('tab-bar__tab--sidebar-hover')
          lastHighlighted = tabEl
        }
      }
    }

    const handleOut = (e: Event) => {
      const me = e as MouseEvent
      if (!sidebar.contains(me.relatedTarget as Node)) {
        if (lastHighlighted) {
          lastHighlighted.classList.remove('tab-bar__tab--sidebar-hover')
          lastHighlighted = null
        }
      }
    }

    sidebar.addEventListener('mouseover', handleOver)
    sidebar.addEventListener('mouseout', handleOut)
    return () => {
      sidebar.removeEventListener('mouseover', handleOver)
      sidebar.removeEventListener('mouseout', handleOut)
      if (lastHighlighted) {
        lastHighlighted.classList.remove('tab-bar__tab--sidebar-hover')
      }
    }
  }, [])

  // Tab hover → sidebar highlight: add/remove CSS class on matching sidebar item via DOM
  const lastHoverElRef = useRef<Element | null>(null)
  const handleTabHover = useCallback((path: string | null) => {
    if (lastHoverElRef.current) {
      lastHoverElRef.current.classList.remove('sidebar-item--tab-hover', 'draggable-area-item--tab-hover')
      lastHoverElRef.current = null
    }
    if (path) {
      const el = document.querySelector(`.standard-sidebar [data-path="${CSS.escape(path)}"]`)
      if (el) {
        const cls = el.classList.contains('draggable-area-item') ? 'draggable-area-item--tab-hover' : 'sidebar-item--tab-hover'
        el.classList.add(cls)
        lastHoverElRef.current = el
      }
    }
  }, [])

  // Promote a preview tab to permanent (used by TabBar double-click on preview tab)
  const handlePromotePreview = useCallback((tab: Tab) => {
    promotePreviewTab(tab.id)
  }, [promotePreviewTab])

  // ⌘L toggles full↔list, ⌘S toggles the stack deck — both only while a project tab is active (the compact views
  // are project-specific) — or to exit when already compact. (`useWindowMode` steht weiter oben.)
  // **Das Fenster wird so schmal wie die Liste** (#296) — breiter nur, solange Canvas oder Aufgaben-Details offen
  // sind; dann wächst es mit (Main-Prozess). Im Listen- und Stapel-Modus gilt deren eigene Mindestgröße.
  const windowMinWidth = mainWindowMinWidth({
    canvasSplit: canvasMode === 'split',
    detailsOpen: detailsPanelOpen,
    detailsListMinWidth,
    pageListMinWidth,
  })
  useEffect(() => {
    if (!isElectron || isCompactMode) return
    window.windowAPI?.setMinWidth?.(windowMinWidth)
  }, [isElectron, isCompactMode, windowMinWidth])

  const activeIsProject = !!activeTab?.path && /\/project\/[^/]+/.test(activeTab.path)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return
      if (e.key === 'l' || e.key === 'L') {
        if (!activeIsProject && !isCompactMode) return
        e.preventDefault()
        toggleListMode()
      }
      if (e.key === 's' || e.key === 'S') {
        if (!activeIsProject && !isCompactMode) return
        e.preventDefault()
        toggleStackMode()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activeIsProject, isCompactMode, toggleListMode, toggleStackMode])

  // ── Expand / mode-switch choreography (list ↔ full) ──
  // Electron: hold the UI hidden across the async OS window resize and reveal
  // once settled. Web: no resize happens — everything swaps in the same paint.
  // Details in useModeSwitchChoreography. `listExiting` fades the compact list
  // out (still list mode) before the window grows; after ~80ms we switch to full.
  const [listExiting, setListExiting] = useState(false)
  const { fullPhase, onBodyRevealEnd } = useModeSwitchChoreography(
    isCompactMode,
    isElectron,
    tabBarSlotRef,
  )

  // Expand button: fade the list out first, then switch to full mode.
  const handleExpand = useCallback(() => {
    setListExiting(true)
    window.setTimeout(() => {
      setListExiting(false)
      switchToFullMode()
    }, 80)
  }, [switchToFullMode])

  // Bridge the expand action to WindowControls (Windows), which lives above the
  // WindowModeProvider and can't reach handleExpand directly.
  useEffect(() => {
    exitToFullRef.current = handleExpand
    return () => { exitToFullRef.current = null }
  }, [handleExpand, exitToFullRef])

  // TabBar JSX — rendered via portal into Titlebar slot
  const tabBarElement = (
    <TabBar
      tabs={tabs}
      activeTab={activeTab}
      onTabClick={setActiveTab}
      onTabClose={closeTab}
      onTabPin={pinTab}
      onCloseOthers={closeOtherTabs}
      onReorder={reorderTabs}
      sidebarLeftOpen={isNavVisible}
      sidebarRightOpen={rightSidebarVisible}
      canCloseLastTab={isSecondaryWindow}
      tabBarRef={tabBarRef}
      onTabHover={handleTabHover}
      onPromotePreview={handlePromotePreview}
    />
  )

  // Compact-mode bar — replaces the TabBar in the titlebar slot while in
  // list OR stack mode. Rendered here (not in the Titlebar) so it has the
  // tab/navigation context: project switcher + stack toggle + expand-to-full.
  const listLifeSpace = activeTab?.lifeSpace ?? activeLifeSpace
  // Stop at `?`/`#` — project paths can carry query params (?section=… from
  // scroll-spy / sidebar section links) which must not pollute the id.
  const currentProjectId = activeTab?.path.match(/\/project\/([^/?#]+)/)?.[1]
  // Für die Schnelleingabe (#234): Bei zwei Projekten „Test“ meint `#Test` das hier offene.
  useEffect(() => { rememberOpenProject(currentProjectId ?? null) }, [currentProjectId])

  // Remember the last visited project per life space (any window mode) — the
  // compact ProjectSwitcher's space menu jumps back to it on a space switch.
  // Keyed by the PROJECT's own space (tab membership can drift, e.g. tabs
  // opened from "Alle" or after a project moved).
  const rememberProject = useLiveQuery(
    () => (currentProjectId ? db.getProject(currentProjectId) : null),
    [currentProjectId],
  )
  useEffect(() => {
    if (rememberProject && !rememberProject.deletedAt) {
      rememberLastProject(rememberProject.lifeSpace, rememberProject.id)
      // **Zuletzt geöffnet** (#233): ein Projekt, sobald sein Tab vorn steht.
      recordRecent({ kind: 'project', id: rememberProject.id })
    }
  }, [rememberProject])

  /* ── Schnelleingabe (#234): Q — auf dem Desktop das eigene Fenster, im Web ein Dialog ── */
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const openQuickAdd = useCallback(() => {
    if (isElectron) void window.windowAPI?.openQuickAdd?.()
    else setQuickAddOpen(true)
  }, [isElectron])
  useQuickAddKey(openQuickAdd)
  // Das „+“ in der Titelleiste (#256) — die Titelleiste steht über diesem Layout.
  useEffect(() => onAppEvent('open-quick-add', openQuickAdd), [openQuickAdd])

  /* ── Suche (#233): Lupe, ⌘K / Strg+K, Menü „Suchen …“ ── */
  const [searchOpen, setSearchOpen] = useState(false)
  // Im Listen- und Stapel-Modus gibt es keine Tabs, in die ein Treffer springen könnte.
  useCommandPaletteShortcut(() => setSearchOpen(open => !open), !isCompactMode)
  useEffect(() => onAppEvent('open-search', () => setSearchOpen(true)), [])

  const playTask = usePlayTask()
  const searchSource = useDataSource()
  const searchProjects = searchSource.useProjects()

  /*
    **Der Seitenverlauf** (#225, ADR 0034, CONTEXT.md): eine Liste besuchter Seiten über alle
    Lebensbereiche und Tabs. Ein Besuch ist jeder Wechsel der Seite im aktiven Tab — gleich, ob über
    Sidebar, Suche, Tab-Leiste, Umschalter, Link oder Lebensbereich. Nur im Hauptfenster.
  */
  const currentVisit = useMemo<PageVisit | null>(
    () => (activeTab ? { lifeSpace: activeTab.lifeSpace, path: activeTab.path, title: activeTab.title, iconName: serializeTab(activeTab).iconName } : null),
    [activeTab],
  )
  // Vor dem Sprung nachschlagen: Ein gelöschtes Projekt fällt aus, ein umgezogenes erscheint dort,
  // wo es JETZT liegt (#220), mit seinem aktuellen Namen.
  const resolveVisit = useCallback(async (visit: PageVisit): Promise<PageVisit | null> => {
    const projectId = visit.path.match(/\/project\/([^/?#]+)/)?.[1]
    if (!projectId) return visit
    const project = await db.projects.get(projectId)
    if (!project || project.deletedAt) return null
    return { ...visit, lifeSpace: project.lifeSpace, path: `/home/${project.lifeSpace}/project/${projectId}`, title: project.name }
  }, [])
  const showVisit = useCallback((visit: PageVisit) => {
    setActiveLifeSpace(visit.lifeSpace)
    showPage({ path: visit.path, title: visit.title, icon: deserializeTab({ id: '', ...visit }).icon, lifeSpace: visit.lifeSpace })
  }, [setActiveLifeSpace, showPage])
  const pageHistory = useNavigationHistory({ current: currentVisit, enabled: !isSecondaryWindow, resolve: resolveVisit, show: showVisit })

  // ⌘← / ⌘→ (Strg+← / →): nicht im Textfeld, nicht bei offenem Dialog oder offener Suche, und nicht
  // im Listen- oder Stapel-Fenster — dort gibt es keinen Ort für einen Sprung (Sven, 04.10.2026).
  const pageHistoryKeysOn = !isSecondaryWindow && !isCompactMode && !searchOpen
  useEffect(() => {
    if (!pageHistoryKeysOn) return
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return
      if (e.code !== 'ArrowLeft' && e.code !== 'ArrowRight') return
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return
      e.preventDefault()
      void (e.code === 'ArrowLeft' ? pageHistory.back() : pageHistory.forward())
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pageHistoryKeysOn, pageHistory])

  // Die Pfeile der Titlebar: Sprünge als Ref, das Ausgrauen und die Listen als State.
  const projectNames = useMemo(() => new Map((searchProjects ?? []).map(project => [project.id, project.name])), [searchProjects])
  useEffect(() => {
    pageHistoryRef.current = {
      back: () => { void pageHistory.back() },
      forward: () => { void pageHistory.forward() },
      goTo: index => { void pageHistory.goTo(index) },
    }
    const labels = {
      projectNames,
      iconFor: (iconName: string | undefined) => deserializeTab({ id: '', path: '', title: '', lifeSpace: '', iconName }).icon,
      lifeSpaceLabel: (lifeSpace: string) => t(`lifeSpaces.${lifeSpace}`),
      untitledProject: t('project.defaultName'),
    }
    const label = (list: typeof pageHistory.backList) => labelPageHistory(list, labels)
    setPageHistory({
      canGoBack: pageHistory.canGoBack && !isCompactMode,
      canGoForward: pageHistory.canGoForward && !isCompactMode,
      backList: label(pageHistory.backList),
      forwardList: label(pageHistory.forwardList),
    })
  }, [pageHistory, pageHistoryRef, setPageHistory, projectNames, isCompactMode, t])
  useEffect(() => () => { pageHistoryRef.current = null }, [pageHistoryRef])
  const handleSearchSelect = useCallback((hit: SearchHit, { newTab }: { newTab: boolean }) => {
    const describe = (target: SearchTarget) => {
      if (target.kind === 'project') {
        const name = searchProjects?.find(p => p.id === target.projectId)?.name
        return { title: name || t('project.defaultName'), icon: AreaIcon as unknown as LucideIcon }
      }
      const page = SEARCH_PAGE_TABS[target.page]
      return { title: t(page.titleKey), icon: page.icon }
    }
    void openSearchHit(hit, { newTab }, {
      activeLifeSpace,
      openTab,
      openPreviewTab,
      switchLifeSpace: (lifeSpace: LifeSpace) => {
        setActiveLifeSpace(lifeSpace)
        switchToSpace(lifeSpace)
      },
      getTaskAncestors: id => searchSource.getTaskAncestors(id),
      describe,
    })
  }, [searchProjects, activeLifeSpace, openTab, openPreviewTab, setActiveLifeSpace, switchToSpace, searchSource, t])
  useEffect(() => onAppEvent('open-task', ({ task }) => handleSearchSelect({ kind: 'task', task }, { newTab: false })), [handleSearchSelect])

  const compactBarElement = (
    <CompactListBar
      mode={isStackMode ? 'stack' : 'list'}
      lifeSpace={listLifeSpace}
      currentProjectId={currentProjectId}
      currentProjectName={activeTab?.title ?? ''}
      onSelectProject={(project) =>
        // Path + tab membership derive from the PROJECT's life space (not the
        // current one) so the space menu's cross-space jump works in place.
        navigateActiveTab({
          path: `/home/${project.lifeSpace}/project/${project.id}`,
          title: project.name || t('project.defaultName'),
          icon: AreaIcon as any,
          lifeSpace: project.lifeSpace,
        })
      }
      onLifeSpaceChange={setActiveLifeSpace}
      onExit={handleExpand}
      onSwitchToStack={switchToStackMode}
      onSwitchToList={() => switchToListMode(null)}
      showAlwaysOnTop={isElectron}
      alwaysOnTop={alwaysOnTop}
      onToggleAlwaysOnTop={toggleAlwaysOnTop}
    />
  )

  return (
    <div className={`main-layout${isCompactMode ? ' main-layout--list-mode' : ''}${isStackMode ? ' main-layout--stack-mode' : ''}${fullPhase === 'entering' ? ' main-layout--full-entering' : ''}${fullPhase === 'revealing' ? ' main-layout--full-revealing' : ''}${listExiting ? ' main-layout--list-exiting' : ''}${!isNavVisible ? ' main-layout--nav-hidden' : ''}${canvasMode === 'split' || canvasMode === 'maximized' || canvasMode === 'closing' ? ' main-layout--canvas-motion' : ''}${isPeekActive ? ' main-layout--sidebar-peek' : ''}${peekExit === 'leaving' ? ' main-layout--peek-leaving' : ''}${peekExit === 'docking' ? ' main-layout--peek-docking' : ''}${rightSidebarMotion ? ' main-layout--right-sidebar-motion' : ''}`}>
      {/* Titlebar slot: the TabBar normally, the compact bar in list/stack mode. */}
      {tabBarSlotReady && tabBarSlotRef.current && createPortal(
        isCompactMode ? compactBarElement : tabBarElement,
        tabBarSlotRef.current,
      )}

      {/* ContentUnionShape: bump layers → union-bump-slot (before titlebar in DOM),
          border → union-border-slot (after content in DOM).
          DOM-order stacking ensures labels always paint above bump. */}
      {(bumpSlotReady || unionShapeSlotReady) && (
        <ContentUnionShape
          contentRef={contentRef}
          activeTabId={activeTab?.id}
          isFirstTabActive={isFirstTabActive}
          isPreview={activeTab?.isPreview}
          sidebarLeftOpen={isNavVisible}
          sidebarRightOpen={rightSidebarVisible}
          tabCount={tabs.length}
          pinnedCount={pinnedCount}
          bumpSlotEl={bumpSlotRef.current}
          borderSlotEl={unionShapeSlotRef.current}
          isListMode={isCompactMode}
        />
      )}

      {/* App Content */}
      <div
        ref={bodyRef}
        className="main-layout__body"
        onAnimationEnd={(e) => {
          // Clear the fade phase once the body's own reveal animation ends
          // (ignore descendant animations — e.g. task entries — that bubble up).
          if (e.target === e.currentTarget) onBodyRevealEnd()
        }}
      >
        {/* Both rails share one wrapper so the canvas focus mode can slide
            them out as a single unit (and the hover peek can lift them out of
            the flex flow without the two drifting apart). */}
        <div
          ref={navRef}
          className="main-layout__nav"
          // Hover hält den Peek offen — native Listener, siehe `navRef`-Effekt.
          // Ausgeblendet heißt 0px breit, nicht ausgehängt — die Leisten blieben
          // sonst per Tab erreichbar, obwohl nichts zu sehen ist.
          inert={!isNavVisible && !isPeekActive}
        >
        <LifeSpacesSidebar
          activeSpace={activeLifeSpace}
          onSpaceChange={handleLifeSpaceChange}
        />

        {/* Die Sidebar rendert immer vollständig — ob man sie sieht, entscheidet
            allein der Wrapper. Sie kennt ihre eigene Sichtbarkeit bewusst nicht
            mehr, seit die Icon-Leiste weg ist (ADR 0005); der Peek muss sie
            dadurch auch nicht mehr künstlich auf „offen" zwingen. */}
        <StandardSidebar
          lifeSpace={activeLifeSpace}
          activeTab={activeTab}
          tabs={tabs}
          openTab={openTab}
          navigateActiveTab={navigateActiveTab}
          openPreviewTab={openPreviewTab}
          promotePreviewBySpace={promotePreviewBySpace}
          onCloseTabByPath={tabs.length > 1 ? closeTabByPath : undefined}
          onUnpinByPath={(path: string) => {
            const tab = tabs.find(t => t.path === path && t.isPinned)
            if (tab) pinTab(tab)
          }}
          onPinByPath={(path: string) => {
            const tab = tabs.find(t => t.path === path && !t.isPinned)
            if (tab) pinTab(tab)
          }}
        />

        </div>

        {/* Die Update-Karte steht NEBEN der Navigation, nicht darin.
            Der Wrapper oben trägt `inert`, sobald die Leisten ausgeblendet
            sind — richtig für die Leisten, die dann nicht mehr per Tab
            erreichbar sein sollen, fatal für diese Karte: Sie ragt sichtbar
            heraus und wäre trotzdem tot, weil `inert` den ganzen Teilbaum
            still schaltet (kein Klick, kein Fokus). Genau das war der Fall —
            bei eingeklappter Sidebar liessen sich weder das X noch der Knopf
            drücken.

            Die Lage ändert sich dadurch nicht: `main-layout__body` ist
            ebenfalls `position: relative` und beginnt an derselben linken
            Kante wie `main-layout__nav`. */}
        <UpdateSidebarOverlay navigationVisible={isNavVisible} />

        {/* Content Area */}
        {/* `--canvas-open` statt eines `:has()`-Selektors im SCSS: Der Zustand
            liegt hier ohnehin vor, und eine echte Klasse ist im Test prüfbar
            und im DevTools sichtbar. Die frühere CSS-only-Fassung hing an
            zwei Annahmen gleichzeitig (matcht `:has()`, und stimmt die
            Stapelreihenfolge) — schlägt eine fehl, passiert stumm nichts. */}
        <div ref={contentRef} className={`main-layout__content${
          !rightSidebarVisible ? ' main-layout__content--no-right-sidebar' : ''
        }${isCanvasFocused ? ' main-layout__content--canvas-open' : ''}${canvasMode === 'maximized' ? ' main-layout__content--canvas-maximized' : ''}${canvasMode === 'peek' ? ' main-layout__content--canvas-peek' : ''}${canvasMode === 'closing' ? ' main-layout__content--canvas-closing' : ''}${detailsPanelOpen ? ' main-layout__content--details-open' : ''}`}>
          <BreadcrumbTitleProvider>
            <MainBreadcrumb
              lifeSpace={activeLifeSpace}
              spaceName={activeLifeSpace === 'all' ? t('lifeSpaces.allBreadcrumb') : spaces.find(s => s.id === activeLifeSpace)?.name ?? ''}
              sectionTitle={getSectionTitle(activeTab?.path, t)}
              trail={breadcrumbLevels}
              activeProjectId={breadcrumbProject?.projectId ?? null}
            />
            <div className="main-layout__scroll-container">
              <div
                ref={scrollRef}
                className={`main-layout__scroll-wrap${scrollbarVisible ? ' main-layout__scroll-wrap--visible' : ''}`}
                onScroll={handleContentScroll}
              >
                <TabManagerProvider
                  tabs={tabs}
                  activeTabId={activeTab?.id ?? null}
                  activeTab={activeTab}
                  updateTab={updateTab}
                  closeActiveTab={closeActiveTab}
                  openTab={openTab}
                >
                  {children}
                </TabManagerProvider>
              </div>
            </div>
          </BreadcrumbTitleProvider>
        </div>

        {/* Nur auf „Ziele": dort ist sie ein Hilfe-Panel zur Seite, überall sonst
            waren es Widgets ohne Bezug zum Inhalt daneben. */}
        {rightSidebarAvailable && (
          <RightSidebar
            isOpen={uiState.sidebarRight}
            onToggle={toggleRightSidebar}
            activeTabPath={activeTab?.path}
          />
        )}
      </div>

      {/* Der Geschafft-Moment (Kernschleife 3c) — EINMAL je Hauptfenster.
          Hier und nicht in `App.tsx`: Das Fokus-Fenster benutzt `MainLayout`
          nicht, also braucht die Pille keine Ausnahme für sich. */}
      <DoneMomentHost />

      <TestDataConfirmDialog {...testDataDialog} />

      <CommandPaletteDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        activeLifeSpace={activeLifeSpace}
        lifeSpaces={spaces}
        onSelect={handleSearchSelect}
        onStartFocus={playTask}
      />

      {!isElectron && (
        <QuickAddDialog open={quickAddOpen} onOpenChange={setQuickAddOpen} lifeSpace={activeLifeSpace} />
      )}

      {/* „Entdecken“ (#323): die Erklärung am Ziel — eine für das ganze Fenster. */}
      <DiscoverTip />

      {/* Das Fokus-Fenster im Web (Kernschleife 3d) — unter Electron zeichnet sie
          nie, dort hat die Pille ihr eigenes Fenster. */}
      <FocusOverlayHost />
    </div>
  )
}

// Memoize MainLayout to prevent unnecessary re-renders
const MainLayout = React.memo(MainLayoutComponent)

export default MainLayout